<template>
  <div>
    <DxPopup
      :visible="visible"
      @hiding="onHiding"
      @shown="popupShown"
      :width="'80%'"
      :height="getPopupHeight"
      :show-title="true"
      :title="getPopupTitle"
      :drag-enabled="false"
      :hide-on-outside-click="false"
      :show-close-button="false"
      :wrapperAttr="{ class: 'css-v2 gc-popup gc-popup-email-direct' }"
    >
      <div class="css-v2 gc-popup-form gc-email-direct-form" ref="mainDiv">
        <div class="inner-content">
          <!-- Email Address Section -->
          <div class="gc-form-section">
            <label class="gc-field-label">
              <i class="dx-icon-email gc-label-icon"></i>
              To (Email Address)
            </label>

            <!-- Tag input + search dropdown wrapper -->
            <div class="gc-email-input-wrapper" v-click-outside="closeDropdown">
              <div class="gc-email-tags-container" @click="focusEmailInput">
                <span
                  v-for="(email, index) in selectedEmails"
                  :key="'tag-' + index"
                  class="gc-email-tag"
                >
                  {{ email }}
                  <span
                    class="gc-email-tag-remove"
                    @click.stop="removeEmail(index)"
                    >×</span
                  >
                </span>
                <input
                  ref="emailInput"
                  v-model="manualEmailInput"
                  @keydown.enter.prevent="onEnterKey"
                  @keydown.tab="onTabKey"
                  @keydown.prevent.188="addManualEmail"
                  @keydown.down.prevent="moveDropdownFocus(1)"
                  @keydown.up.prevent="moveDropdownFocus(-1)"
                  @keydown.esc="closeDropdown"
                  @input="onEmailInput"
                  placeholder="Type email and press Enter..."
                  class="gc-email-inline-input"
                />
              </div>

              <!-- Search dropdown -->
              <div
                v-if="showDropdown && filteredContacts.length > 0"
                class="gc-search-dropdown"
                @wheel.stop
              >
                <div
                  v-for="(contact, idx) in filteredContacts"
                  :key="contact.id"
                  class="gc-search-dropdown-item"
                  :class="{
                    'gc-search-dropdown-item--focused':
                      dropdownFocusIndex === idx,
                    'gc-search-dropdown-item--selected':
                      isContactSelected(contact),
                  }"
                  @mousedown.prevent="selectDropdownContact(contact)"
                >
                  <span class="gc-contact-avatar gc-contact-avatar--sm">{{
                    getInitials(contact.name)
                  }}</span>
                  <div class="gc-contact-info">
                    <span
                      class="gc-contact-name"
                      v-html="highlight(contact.name)"
                    ></span>
                    <span
                      class="gc-contact-email"
                      v-html="highlight(contact.email)"
                    ></span>
                  </div>
                  <i
                    v-if="isContactSelected(contact)"
                    class="dx-icon-check gc-dropdown-check"
                  ></i>
                </div>
              </div>
            </div>

            <!-- Fixed-height error slot — never causes layout shift -->
            <div class="gc-field-error-slot" v-if="emailError">
              <span v-if="emailError" class="gc-field-error">{{
                emailError
              }}</span>
            </div>
          </div>

          <!-- Contact List Section -->
          <div class="gc-form-section">
            <div class="gc-contact-list-header">
              <label class="gc-field-label">
                <i class="dx-icon-group gc-label-icon"></i>
                Select Contacts
                <span class="gc-label-hint"
                  >(optional — select one or more)</span
                >
              </label>
              <!-- Hide Select All when there are no contacts to select -->
              <button
                v-if="contacts.length > 0"
                class="gc-select-all-btn"
                :class="{
                  'gc-select-all-btn--all': allContactsSelected,
                  'gc-select-all-btn--partial':
                    someContactsSelected && !allContactsSelected,
                }"
                @click="toggleSelectAll"
              >
                <i
                  :class="
                    allContactsSelected
                      ? 'dx-icon-check'
                      : someContactsSelected
                      ? 'dx-icon-minus'
                      : 'dx-icon-square'
                  "
                ></i>
                {{ allContactsSelected ? "Deselect All" : "Select All" }}
              </button>
            </div>

            <div class="gc-contact-list" @wheel.stop>
              <!-- Empty state -->
              <div v-if="contacts.length === 0" class="gc-contact-list-empty">
                <i class="dx-icon-group gc-contact-list-empty-icon"></i>
                <span class="gc-contact-list-empty-title"
                  >No contacts available</span
                >
                <span class="gc-contact-list-empty-hint">
                  No contacts were found for this record. You can still send the
                  email by entering an address manually in the field above.
                </span>
              </div>

              <!-- Contact rows -->
              <div
                v-for="contact in contacts"
                :key="contact.id"
                class="gc-contact-item"
                :class="{
                  'gc-contact-item--selected': isContactSelected(contact),
                }"
                @click="toggleContact(contact)"
              >
                <span class="gc-contact-avatar">{{
                  getInitials(contact.name)
                }}</span>
                <div class="gc-contact-info">
                  <span class="gc-contact-name">{{ contact.name }}</span>
                  <span class="gc-contact-email">{{ contact.email }}</span>
                </div>
                <span class="gc-contact-check">
                  <i class="dx-icon-check"></i>
                </span>
              </div>
            </div>
          </div>

          <!-- Subject Section -->
          <div class="gc-form-section">
            <label class="gc-field-label">
              <i class="dx-icon-tips gc-label-icon"></i>
              Subject
            </label>
            <DxTextBox
              v-model="subject"
              :input-attr="{ class: 'gc-dx-input' }"
              styling-mode="outlined"
              class="gc-dx-textbox"
              placeholder="Enter email subject here"
              ref="subjectInput"
            />
          </div>

          <!-- Body Section -->
          <div class="gc-form-section gc-form-section--grow">
            <label class="gc-field-label">
              <i class="dx-icon-comment gc-label-icon"></i>
              Email Body
            </label>
            <gc-html-editor
              :value="emailBody"
              height="168px"
              placeholder="Enter email body here"
              class="gc-email-body-editor"
              ref="bodyInput"
              @value-changed="(e) => (emailBody = e.value)"
            />
          </div>

          <!-- Action Buttons -->
          <div class="gc-action-buttons">
            <DxButton
              text="Cancel"
              icon="close"
              styling-mode="outlined"
              class="gc-btn-cancel"
              @click="onClickCancel"
            />
            <DxButton
              text="Send Email"
              icon="email"
              styling-mode="contained"
              class="gc-btn-send"
              @click="sendEmail"
              :disabled="!!emailError"
            />
          </div>
        </div>
      </div>
    </DxPopup>
  </div>
</template>

<script>
import { DxPopup } from "devextreme-vue/popup";
import { DxTextBox } from "devextreme-vue/text-box";
import { DxButton } from "devextreme-vue/button";
import GcHtmlEditor from "@/components/common/gc-html-editor";
import { mapGetters } from "vuex";

export default {
  name: "EmailDirectPopup",
  components: { DxPopup, DxTextBox, DxButton, GcHtmlEditor },

  // v-click-outside — closes dropdown when clicking outside the wrapper
  directives: {
    clickOutside: {
      beforeMount(el, binding) {
        el._clickOutsideHandler = (e) => {
          if (!el.contains(e.target)) binding.value(e);
        };
        document.addEventListener("mousedown", el._clickOutsideHandler);
      },
      unmounted(el) {
        document.removeEventListener("mousedown", el._clickOutsideHandler);
      },
    },
  },

  props: {
    visible: { type: Boolean, default: false },
    showLoading: { type: Function, default: () => {} },
    emailModel: { type: Object, default: null },
    invoiceNumber: { type: [String, Number], default: "" },
  },

  data() {
    return {
      popupTitle: "Email ",
      manualEmailInput: "",
      selectedEmails: [],
      selectedContactIds: [],
      contacts: [],
      subject: undefined,
      emailBody: undefined,
      emailError: "",
      showDropdown: false,
      dropdownFocusIndex: -1,
    };
  },

  computed: {
    ...mapGetters({ getHotKeySave: "hotKey/getHotKeySave" }),

    filteredContacts() {
      const q = (this.manualEmailInput || "").trim().toLowerCase();
      if (!q) return [];
      return this.contacts.filter(
        (c) =>
          c.name.toLowerCase().includes(q) || c.email.toLowerCase().includes(q),
      );
    },

    getPopupHeight() {
      const baseHeight = 620;
      const additionalHeight = this.emailError ? 16 : 0;
      return baseHeight + additionalHeight;
    },

    allContactsSelected() {
      return (
        this.contacts.length > 0 &&
        this.contacts.every((c) => this.selectedContactIds.includes(c.id))
      );
    },

    someContactsSelected() {
      return this.contacts.some((c) => this.selectedContactIds.includes(c.id));
    },

    getPopupTitle() {
      if (this.invoiceNumber) {
        return this.popupTitle + ` – ${this.invoiceNumber}`;
      } else {
        return this.popupTitle;
      }
    },
  },

  methods: {
    /*
     * Toggles selection of all contacts in the list. If all are currently selected, it will deselect all.
     * If some or none are selected, it will select all.
     */
    toggleSelectAll() {
      if (this.allContactsSelected) {
        const contactEmails = this.contacts.map((c) => c.email);
        this.selectedContactIds = [];
        this.selectedEmails = this.selectedEmails.filter(
          (e) => !contactEmails.includes(e),
        );
      } else {
        this.contacts.forEach((c) => {
          if (!this.selectedContactIds.includes(c.id)) {
            this.selectedContactIds.push(c.id);
          }
          if (!this.selectedEmails.includes(c.email)) {
            this.selectedEmails.push(c.email);
          }
        });
      }
    },

    /*
     * Handles input in the email field. Resets error messages and manages dropdown visibility.
     */
    onEmailInput() {
      this.emailError = "";
      this.dropdownFocusIndex = -1;
      this.showDropdown = this.manualEmailInput.trim().length > 0;
    },

    /*
     * Handles the Enter key press in the email input. If a dropdown item is focused, it selects that contact.
     * Otherwise, it attempts to add the manual email input as a recipient.
     */
    onEnterKey() {
      if (this.showDropdown && this.dropdownFocusIndex >= 0) {
        const contact = this.filteredContacts[this.dropdownFocusIndex];
        if (contact) {
          this.selectDropdownContact(contact);
          return;
        }
      }
      this.addManualEmail();
    },

    /*
     * Handles the Tab key press in the email input. If the dropdown is open and has items, it selects the first contact.
     * Otherwise, it attempts to add the manual email input as a recipient.
     */
    onTabKey() {
      if (this.showDropdown && this.filteredContacts.length > 0) {
        this.selectDropdownContact(this.filteredContacts[0]);
      } else {
        this.addManualEmail();
      }
    },

    /*
     * Moves the focus up or down in the dropdown list based on the provided delta.
     * Delta should be +1 to move down and -1 to move up. The focus will wrap within the bounds of the list.
     */
    moveDropdownFocus(delta) {
      if (!this.showDropdown || !this.filteredContacts.length) return;
      const max = this.filteredContacts.length - 1;
      this.dropdownFocusIndex = Math.min(
        Math.max(this.dropdownFocusIndex + delta, 0),
        max,
      );
    },

    /**
     * Handles selection of a contact from the dropdown. Toggles the contact's selection state,
     * clears the manual email input and error, closes the dropdown, and focuses back on the email input.
     * @param {Object} contact - The contact object that was selected from the dropdown.
     */
    selectDropdownContact(contact) {
      this.toggleContact(contact);
      this.manualEmailInput = "";
      this.emailError = "";
      this.closeDropdown();
      this.$refs.emailInput && this.$refs.emailInput.focus();
    },

    /**
     * Closes the email input dropdown and resets the focus index.
     */
    closeDropdown() {
      this.showDropdown = false;
      this.dropdownFocusIndex = -1;
    },

    /**
     * Focuses the email input field.
     */
    focusEmailInput() {
      this.$refs.emailInput && this.$refs.emailInput.focus();
    },

    /**
     * Focuses the subject input field.
     */
    focusSubjectInput() {
      const ref = this.$refs.subjectInput;
      if (ref && ref.$el) {
        const el = ref.$el.querySelector("input");
        if (el) el.focus();
      }
    },

    /**
     * Focuses the body input field.
     */
    focusBodyInput() {
      const ref = this.$refs.bodyInput;
      if (ref && ref.$el) {
        const el = ref.$el.querySelector(".dx-htmleditor-content");
        if (el) el.focus();
      }
    },

    /**
     * Adds the manual email input as a recipient if it is valid.
     */
    addManualEmail() {
      const raw = (this.manualEmailInput || "").trim().replace(/,$/, "");
      if (!raw) return;
      if (!this.isValidEmail(raw)) {
        this.emailError = `"${raw}" is not a valid email address.`;
        return;
      }
      if (!this.selectedEmails.includes(raw)) {
        this.selectedEmails.push(raw);
      }
      this.manualEmailInput = "";
      this.emailError = "";
      this.closeDropdown();
    },

    /**
     * Removes an email from the selected emails list based on its index. If the removed email is associated with a contact,
     * it also deselects that contact.
     * @param {number} index - The index of the email to be removed from the selected emails list.
     */
    removeEmail(index) {
      const removed = this.selectedEmails[index];
      this.selectedEmails.splice(index, 1);
      const contact = this.contacts.find((c) => c.email === removed);
      if (contact) {
        this.selectedContactIds = this.selectedContactIds.filter(
          (id) => id !== contact.id,
        );
      }
    },

    /**
     * Validates if the provided email address is in a correct format.
     * @param {string} email - The email address to validate.
     * @returns {boolean} - Returns true if the email is valid, otherwise false.
     */
    isValidEmail(email) {
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    },

    /**
     * Checks if a contact is selected.
     * @param {Object} contact - The contact object to check.
     * @returns {boolean} - Returns true if the contact is selected, otherwise false.
     */
    isContactSelected(contact) {
      return this.selectedContactIds.includes(contact.id);
    },

    /**
     * Toggles the selection state of a contact.
     * @param {Object} contact - The contact object to toggle.
     */
    toggleContact(contact) {
      if (this.isContactSelected(contact)) {
        this.selectedContactIds = this.selectedContactIds.filter(
          (id) => id !== contact.id,
        );
        this.selectedEmails = this.selectedEmails.filter(
          (e) => e !== contact.email,
        );
      } else {
        this.selectedContactIds.push(contact.id);
        if (!this.selectedEmails.includes(contact.email)) {
          this.selectedEmails.push(contact.email);
        }
      }
    },

    /**
     * Gets the initials from a name.
     * @param {string} name - The name to extract initials from.
     * @returns {string} - The initials of the name.
     */
    getInitials(name) {
      return name
        .split(" ")
        .map((n) => n[0])
        .slice(0, 2)
        .join("")
        .toUpperCase();
    },

    /**
     * Strips HTML tags to check for actual text content — the html editor
     * emits "<p><br></p>" for an empty body, which is non-empty as a string.
     * @param {string} html - The HTML string to strip.
     * @returns {string} - The plain text content.
     */
    stripHtml(html) {
      const el = document.createElement("div");
      el.innerHTML = html || "";
      return el.textContent.trim();
    },

    /**
     * Highlights the search query in the given text.
     * @param {string} text - The text to highlight.
     * @returns {string} - The highlighted text.
     */
    highlight(text) {
      const q = (this.manualEmailInput || "").trim();
      if (!q) return text;
      const escaped = q.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
      return text.replace(new RegExp(`(${escaped})`, "gi"), "<mark>$1</mark>");
    },

    async onClickCancel() {
      let confirmLeaving = await this.$confirm(
        "You have unsaved changes. Are you sure you want to leave?",
        "Unsaved changes",
      );

      if (confirmLeaving) {
        this.onHiding();
      }
    },

    /**
     * Removes focus from all input fields in the popup. This is typically called before performing an action like sending an email
     * to ensure that any virtual keyboards are dismissed and that the UI is in a consistent state.
     */
    focusOutFromEditors() {
      let refEmail = this.$refs.emailInput;
      if (refEmail && refEmail.$el) {
        const el = refEmail.$el.querySelector("input");
        if (el) el.blur();
      }

      let refSubject = this.$refs.subjectInput;
      if (refSubject && refSubject.$el) {
        const el = refSubject.$el.querySelector("input");
        if (el) el.blur();
      }

      let refBody = this.$refs.bodyInput;
      if (refBody && refBody.$el) {
        const el = refBody.$el.querySelector(".dx-htmleditor-content");
        if (el) el.blur();
      }
    },

    /**
     * Sends the email after validating the inputs.
     */
    async sendEmail() {
      this.addManualEmail();
      this.focusOutFromEditors();

      if (this.selectedEmails.length === 0) {
        await this.$alert(
          "Please add at least one recipient.",
          "Validation Error",
          {
            confirmButtonText: "OK",
          },
        );
        this.focusEmailInput();
        return;
      }
      if (!this.subject) {
        await this.$alert(
          "Subject cannot be empty. Please enter a subject for the email.",
          "Validation Error",
          {
            confirmButtonText: "OK",
          },
        );
        this.focusSubjectInput();
        return;
      }

      if (!this.stripHtml(this.emailBody)) {
        this.$alert(
          "Email body cannot be empty. Please enter the content for the email.",
          "Validation Error",
          {
            confirmButtonText: "OK",
          },
        );

        this.focusBodyInput();
        return;
      }

      const payload = {
        SelectedContactIds: this.selectedContactIds,
        ManualAddresses: this.selectedEmails
          .filter((e) => !this.contacts.some((c) => c.email === e))
          .join(","),
        Subject: this.subject,
        Body: this.emailBody,
      };

      this.$emit("send-email-success", payload);
    },

    /*
     * Handles the hiding of the popup. It resets the hotkey mode, emits an event to update the visibility, and resets the form.
     */
    onHiding() {
      this.$store.dispatch("hotKey/setHotKeyPopupMode", false);
      this.$emit("update:visible", false);
      this.resetForm();
    },

    /*
     * Handles the logic to be executed when the popup is shown. It initializes the form fields based on the email model and sets the hotkey mode.
     */
    popupShown() {
      if (this.emailModel) {
        if (this.emailModel.Contacts) {
          this.contacts = this.emailModel.Contacts.map((c) => ({
            id: c.RecordId,
            name: c.FullName,
            email: c.Email,
          }));
        }

        if (this.emailModel.Subject) {
          this.subject = this.emailModel.Subject;
        }

        if (this.emailModel.Body) {
          this.emailBody = this.emailModel.Body;
        }

        if (this.emailModel.itemData) {
          this.popupTitle += this.emailModel.itemData.ReportDisplayName;
        }
      }
      this.$nextTick(() => {
        this.$store.dispatch("hotKey/setHotKeyPopupMode", true);
        this.focusEmailInput();
      });
    },

    /*
     * Resets the form fields and state to their initial values. This is called when the popup is hidden to ensure a clean state for the next time it is opened.
     */
    resetForm() {
      this.manualEmailInput = "";
      this.selectedEmails = [];
      this.selectedContactIds = [];
      this.contacts = [];
      this.subject = undefined;
      this.emailBody = undefined;
      this.emailError = "";
      this.closeDropdown();
    },
  },

  watch: {
    getHotKeySave: {
      handler(val) {
        if (val) {
          this.$store.dispatch("hotKey/setHotKeySave", false);
          this.$nextTick(() => {
            this.sendEmail();
          });
        }
      },
      deep: true,
    },
  },
};
</script>

<style lang="scss">
.gc-popup-email-direct {
  .inner-content {
    background: white;
    padding: 12px;
    gap: 12px;
    display: flex;
    flex-direction: column;
    // Without this, .inner-content (a flex item of .gc-email-direct-form)
    // never gets a determinate height from the popup's fixed height — it
    // just grows to fit its content instead, so .gc-form-section--grow
    // never actually constrains the Body editor and its internal scroll
    // never engages; the excess is silently clipped by this form's own
    // overflow:hidden instead of being scrollable.
    flex: 1 1 auto;
    min-height: 0;
  }

  .dx-layout-manager.dx-widget {
    padding-right: unset !important;
  }

  .gc-email-direct-form {
    display: flex;
    flex-direction: column;
    height: 100%;
    gap: 10px;
    padding: 4px 2px 0;
    overflow: hidden;
  }

  // ── Generic section ──────────────────────────────────────────────────────
  .gc-form-section {
    display: flex;
    flex-direction: column;
    gap: 4px;

    &--grow {
      flex: 1 1 auto;
      min-height: 0;

      .gc-email-body-editor {
        height: 100% !important;
      }
    }
  }

  .gc-field-label {
    font-size: 11px;
    font-weight: 600;
    color: #555;
    text-transform: uppercase;
    letter-spacing: 0.4px;
    display: flex;
    align-items: center;
    gap: 5px;

    .gc-label-icon {
      font-size: 13px;
      color: #03a9f4;
    }
    .gc-label-hint {
      font-weight: 400;
      text-transform: none;
      color: #999;
      letter-spacing: 0;
    }
  }

  .gc-field-error-slot {
    height: 16px;
    line-height: 16px;
  }

  .gc-field-error {
    font-size: 11px;
    color: #e53935;
  }

  // ── Email input wrapper ───────────────────────────────────────────────────
  .gc-email-input-wrapper {
    position: relative;
  }

  .gc-email-tags-container {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 5px;
    min-height: 34px;
    padding: 5px 8px;
    border: 1px solid #ddd;
    border-radius: 4px;
    background: #fff;
    cursor: text;
    transition: border-color 0.15s;

    &:focus-within {
      border-color: #03a9f4;
      box-shadow: 0 0 0 2px rgba(3, 169, 244, 0.12);
    }
  }

  .gc-email-tag {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 2px 8px;
    background: #e1f5fe;
    color: #0277bd;
    border-radius: 12px;
    font-size: 12px;
    font-weight: 500;
    white-space: nowrap;
  }

  .gc-email-tag-remove {
    cursor: pointer;
    font-size: 14px;
    line-height: 1;
    color: #0288d1;
    margin-left: 2px;
    &:hover {
      color: #e53935;
    }
  }

  .gc-email-inline-input {
    flex: 1;
    min-width: 160px;
    border: none;
    outline: none;
    font-size: 13px;
    background: transparent;
    color: #333;
    &::placeholder {
      color: #bbb;
    }
  }

  // ── Search dropdown ───────────────────────────────────────────────────────
  .gc-search-dropdown {
    position: absolute;
    top: calc(100% + 3px);
    left: 0;
    right: 0;
    z-index: 9999;
    background: #fff;
    border: 1px solid #ddd;
    border-radius: 4px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    max-height: 180px;
    overflow-y: auto;

    &::-webkit-scrollbar {
      width: 4px;
    }
    &::-webkit-scrollbar-thumb {
      background: #ccc;
      border-radius: 2px;
    }
  }

  .gc-search-dropdown-item {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 6px 10px;
    cursor: pointer;
    transition: background 0.1s;

    &:hover,
    &--focused {
      background: #f0f9ff;
    }
    &--selected {
      background: #e1f5fe;
    }

    mark {
      background: #fff9c4;
      color: inherit;
      font-weight: 700;
      border-radius: 2px;
      padding: 0 1px;
    }
  }

  .gc-dropdown-check {
    font-size: 13px;
    color: #03a9f4;
    flex-shrink: 0;
  }

  .gc-contact-avatar--sm {
    width: 24px !important;
    height: 24px !important;
    font-size: 9px !important;
  }

  // ── Contact list header (label + select-all button on same row) ───────────
  .gc-contact-list-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  .gc-select-all-btn {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 2px 8px;
    border: 1px solid #b0bec5;
    border-radius: 10px;
    background: transparent;
    color: #607d8b;
    font-size: 11px;
    font-weight: 600;
    cursor: pointer;
    transition: background 0.12s, border-color 0.12s, color 0.12s;
    white-space: nowrap;

    i {
      font-size: 11px;
    }

    &:hover {
      background: #f0f9ff;
      border-color: #03a9f4;
      color: #0277bd;
    }

    &--partial {
      border-color: #90a4ae;
      color: #546e7a;
    }

    &--all {
      background: #e1f5fe;
      border-color: #03a9f4;
      color: #0277bd;
    }
  }

  // ── Contact list ──────────────────────────────────────────────────────────
  .gc-contact-list {
    display: flex;
    flex-direction: column;
    gap: 3px;
    min-height: 150px;
    max-height: 150px;
    overflow-y: auto;
    border: 1px solid #eee;
    border-radius: 4px;
    padding: 4px;
    background: #fafafa;

    &::-webkit-scrollbar {
      width: 4px;
    }
    &::-webkit-scrollbar-thumb {
      background: #ccc;
      border-radius: 2px;
    }
  }

  // ── Empty state ───────────────────────────────────────────────────────────
  .gc-contact-list-empty {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 4px;
    padding: 8px 16px;
    text-align: center;
  }

  .gc-contact-list-empty-icon {
    font-size: 22px;
    color: #cfd8dc;
    margin-bottom: 2px;
  }

  .gc-contact-list-empty-title {
    font-size: 12px;
    font-weight: 600;
    color: #90a4ae;
  }

  .gc-contact-list-empty-hint {
    font-size: 11px;
    color: #b0bec5;
    line-height: 1.4;
    max-width: 340px;
  }

  // ── Contact items ─────────────────────────────────────────────────────────
  .gc-contact-item {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 5px 8px;
    border-radius: 4px;
    cursor: pointer;
    transition: background 0.12s;
    user-select: none;

    &:hover {
      background: #f0f9ff;
    }

    &--selected {
      background: #e1f5fe;

      .gc-contact-check {
        opacity: 1;
        color: #03a9f4;
      }
    }
  }

  .gc-contact-avatar {
    width: 28px;
    height: 28px;
    border-radius: 50%;
    background: #b3e5fc;
    color: #0277bd;
    font-size: 10px;
    font-weight: 700;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }

  .gc-contact-info {
    flex: 1;
    display: flex;
    flex-direction: column;
    min-width: 0;
  }

  .gc-contact-name {
    font-size: 12px;
    font-weight: 600;
    color: #333;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .gc-contact-email {
    font-size: 11px;
    color: #888;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .gc-contact-check {
    opacity: 0;
    font-size: 14px;
    transition: opacity 0.12s;
    flex-shrink: 0;

    &--partial {
      opacity: 1;
      color: #90a4ae;
    }
  }

  // ── DevExtreme overrides ───────────────────────────────────────────────────
  .gc-dx-textbox {
    .dx-texteditor-input {
      font-size: 13px !important;
      padding: 6px 10px !important;
    }
  }

  .gc-dx-textbox.dx-texteditor.dx-editor-outlined .dx-placeholder::before {
    padding-top: unset !important;
    padding-bottom: unset !important;
  }

  // ── Action buttons ─────────────────────────────────────────────────────────
  .gc-action-buttons {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
    padding-top: 16px;
    padding-bottom: 2px;
    flex-shrink: 0;

    .gc-btn-cancel .dx-button-text {
      font-size: 12px !important;
    }

    .gc-btn-send {
      .dx-button-content {
        background-color: #03a9f4 !important;
        color: #fff !important;
        min-width: 110px;
      }
      .dx-button-text {
        font-size: 12px !important;
        color: #fff !important;
      }
      .dx-icon::before {
        color: #fff !important;
      }
    }
  }
}
</style>
